import { type Order, ShipmentStatus } from "@/api.types.v2";
import { Tooltip } from "@heroui/react";
import { StatusBadge, type StatusTone } from "@/components/status-badge";
import { useTranslation } from "react-i18next";

const tones: Record<ShipmentStatus, StatusTone> = {
  [ShipmentStatus.PENDING_PAYMENT]: "warning",
  [ShipmentStatus.LABEL_READY]: "default",
  [ShipmentStatus.DROPPED_AT_CARRIER]: "secondary",
  [ShipmentStatus.PICKED_UP]: "primary",
  [ShipmentStatus.IN_TRANSIT]: "primary",
  [ShipmentStatus.AT_FACILITY]: "secondary",
  [ShipmentStatus.OUT_FOR_DELIVERY]: "primary",
  [ShipmentStatus.DELIVERED]: "success",
  [ShipmentStatus.AVAILABLE_FOR_PICKUP]: "success",
  [ShipmentStatus.DELIVERY_ATTEMPTED]: "warning",
  [ShipmentStatus.EXCEPTION]: "danger",
  [ShipmentStatus.ON_HOLD]: "default",
  [ShipmentStatus.RETURNED]: "warning",
  [ShipmentStatus.CANCELLED]: "danger",
};

interface ShipmentStatusBadgeProps {
  status: ShipmentStatus | null;
  paymentStatus?: Order["paymentStatus"];
  className?: string;
}

export function ShipmentStatusBadge({
  status,
  paymentStatus,
  className,
}: ShipmentStatusBadgeProps) {
  const { t } = useTranslation("app");

  if (!status) return null;

  const preparing =
    status === ShipmentStatus.PENDING_PAYMENT && paymentStatus === "PAID";
  const badge = (
    <StatusBadge tone={tones[status]} className={className}>
      {t(preparing ? "shipmentStatus.PREPARING" : `shipmentStatus.${status}`, {
        defaultValue: status,
      })}
    </StatusBadge>
  );

  if (status === ShipmentStatus.EXCEPTION || preparing) {
    return (
      <Tooltip
        content={t(
          preparing
            ? "shipmentStatus.preparingTooltip"
            : "shipmentStatus.exceptionTooltip"
        )}
        placement="top"
        classNames={{ content: "max-w-xs" }}
      >
        <span>{badge}</span>
      </Tooltip>
    );
  }

  return badge;
}
